import { QuickBuyDrawer } from 'theme';

class ProductQuickView extends QuickBuyDrawer {
  constructor() {
    super();
    this._externalVideosByHandle = new Map();
    this.addEventListener('click', this._onQuickViewClick.bind(this));
    this.addEventListener('input', this._onQuickViewInput.bind(this));
    this.addEventListener('change', this._onQuickViewInput.bind(this));
    this.addEventListener('keydown', this._onQuickViewKeydown.bind(this));
    this.addEventListener('carousel:change', this._onCarouselChanged.bind(this));
    this.addEventListener('product:rerender', this._beforeProductRerender.bind(this), true);
  }

  async show() {
    if (this._hasLoaded) {
      const result = await super.show();
      this._initializeVisibleContent();
      this._playSelectedMedia(this._activeScope());
      return result;
    }

    this.style.display = 'block';
    const controls = this.controls;
    controls.forEach((control) => control.setAttribute('aria-busy', 'true'));

    try {
      const parentHandle = this.getAttribute('handle');
      const parentData = await this._fetchProductContent(parentHandle);
      const bundleProducts = this._parseBundleProducts(parentData.template);

      if (bundleProducts.length > 1) {
        await this._buildBundleContent(bundleProducts, parentData.template);
      } else {
        this.classList.remove('product-quick-view--bundle');
        this._externalVideos = parentData.externalVideos;
        this.replaceChildren(this._cloneTemplate(parentData.template));
      }

      window.Shopify.PaymentButton?.init();
      this._hasLoaded = true;
      const result = await super.show();
      this._initializeVisibleContent({ mainScrollTop: 0, infoScrollTop: 0 });
      this._playSelectedMedia(this._activeScope());
      return result;
    } catch (error) {
      console.error('[Product quick view]', error);
      this.style.display = 'none';
      window.location.href = this.getAttribute('product-url');
    } finally {
      controls.forEach((control) => control.removeAttribute('aria-busy'));
    }
  }

  async hide() {
    this.querySelectorAll('[data-quick-view-external-video-player]').forEach((player) => player.replaceChildren());
    this.querySelectorAll('video-media, model-media').forEach((media) => media.pause());
    return super.hide();
  }

  async _fetchProductContent(handle) {
    const response = await fetch(window.Shopify.routes.root + 'products/' + handle);
    if (!response.ok) throw new Error(`Unable to load product: ${handle}`);

    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = page.getElementById('product-quick-view-content');
    if (!template) throw new Error(`Quick view content is missing: ${handle}`);
    const fallbackProductImage = page.querySelector('meta[property="og:image"]')?.content
      || template.content.querySelector('.product-quick-view__thumbnail img')?.getAttribute('src')
      || '';
    const externalVideos = Array.from(page.querySelectorAll('.swiper-video-play-btn[data-video-url]'))
      .filter((button) => button.dataset.videoUrl?.trim())
      .map((button) => {
        const slide = button.closest('.swiper-video-slide');
        const thumbnail = slide?.querySelector('.swiper-video-thumb img');
        const videoUrl = button.dataset.videoUrl.trim();
        const srcsetThumbnail = thumbnail?.getAttribute('srcset')?.split(',')[0]?.trim().split(/\s+/)[0] || '';
        const youtubeId = this._getYoutubeId(videoUrl);
        return {
          url: videoUrl,
          thumbnail: thumbnail?.getAttribute('src') || thumbnail?.getAttribute('data-src')
            || thumbnail?.getAttribute('data-lazy-src') || srcsetThumbnail
            || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : fallbackProductImage),
          title: thumbnail?.getAttribute('alt') || 'Product video'
        };
      });
    return { template, externalVideos };
  }

  _cloneTemplate(template) {
    const content = template.content.cloneNode(true);
    content.querySelectorAll('noscript').forEach((node) => node.remove());
    return content;
  }

  _parseBundleProducts(template) {
    const data = template.content.querySelector('[data-quick-view-bundle-products]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent).filter((product) => product?.handle);
    } catch (error) {
      console.warn('[Product quick view] Invalid bundle product data', error);
      return [];
    }
  }

  _parseBundleParent(template) {
    const data = template.content.querySelector('[data-quick-view-bundle-parent]');
    if (!data) return null;
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      console.warn('[Product quick view] Invalid bundle parent data', error);
      return null;
    }
  }

  async _buildBundleContent(products, parentTemplate) {
    const productData = await Promise.all(products.map(async (product) => {
      const handle = product.variantId ? `${product.handle}?variant=${product.variantId}` : product.handle;
      return { product, data: await this._fetchProductContent(handle) };
    }));

    this.classList.add('product-quick-view--bundle');
    this._bundleParent = this._parseBundleParent(parentTemplate);
    if (!this._bundleParent?.variantId) throw new Error('Bundle parent variant is unavailable.');
    this._externalVideosByHandle.clear();

    const tabs = document.createElement('div');
    tabs.slot = 'header';
    tabs.className = 'product-quick-view__bundle-tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Bundle products');
    const shell = document.createElement('div');
    shell.className = 'product-quick-view__bundle-shell';
    const panels = document.createElement('div');
    panels.className = 'product-quick-view__bundle-panels';

    productData.forEach(({ product, data }, index) => {
      const tabId = `${this.id}-bundle-tab-${index}`;
      const panelId = `${this.id}-bundle-panel-${index}`;
      const shortName = product.shortName || product.title;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'product-quick-view__bundle-tab';
      tab.dataset.quickViewBundleTab = product.handle;
      tab.dataset.quickViewBundleName = shortName;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.textContent = shortName;
      tabs.append(tab);

      const panel = document.createElement('section');
      panel.id = panelId;
      panel.className = 'product-quick-view__bundle-panel';
      panel.dataset.quickViewBundlePanel = product.handle;
      panel.dataset.quickViewBundleName = shortName;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.hidden = index !== 0;
      const content = this._cloneTemplate(data.template);
      content.querySelectorAll('[slot="header"], [data-quick-view-bundle-parent], [data-quick-view-bundle-products]').forEach((node) => node.remove());
      panel.append(content);
      panels.append(panel);
      this._externalVideosByHandle.set(product.handle, data.externalVideos);
    });

    shell.append(panels, this._createBundleFeatureDock(), this._createBundlePurchaseBar());
    this.replaceChildren(tabs, shell);
  }

  _createBundleFeatureDock() {
    const dock = document.createElement('div');
    dock.className = 'product-quick-view__bundle-feature-dock';
    dock.dataset.quickViewBundleFeatureDock = '';
    dock.innerHTML = `
      <button
        type="button"
        class="product-quick-view__bundle-feature-toggle"
        data-quick-view-bundle-feature-toggle
        data-more-label="Show more"
        data-less-label="Show less"
        aria-expanded="false"
      >Show more</button>`;
    return dock;
  }

  _createBundlePurchaseBar() {
    const bar = document.createElement('div');
    bar.className = 'product-quick-view__purchase-bar product-quick-view__bundle-purchase';
    bar.innerHTML = `
      <div class="product-quick-view__bundle-summary">
        <div class="product-quick-view__bundle-total">
          <span data-quick-view-bundle-subtotal-label>Total</span>
          <div><strong data-quick-view-bundle-total></strong><s data-quick-view-bundle-compare hidden></s></div>
        </div>
        <p class="product-quick-view__bundle-error" data-quick-view-bundle-error hidden></p>
      </div>
      <div class="product-quick-view__bundle-quantity">
        <div class="quantity-selector">
          <button type="button" class="quantity-selector__button" data-quick-view-bundle-quantity-change="-1" aria-label="Decrease quantity">−</button>
          <input type="text" inputmode="numeric" class="quantity-selector__input" data-quick-view-bundle-quantity value="1" size="2" autocomplete="off" aria-label="Quantity">
          <button type="button" class="quantity-selector__button" data-quick-view-bundle-quantity-change="1" aria-label="Increase quantity">+</button>
        </div>
      </div>
      <div class="product-quick-view__actions product-quick-view__bundle-actions">
        <button type="button" class="button button--lg product-quick-view__bundle-add" data-quick-view-bundle-add>Add bundle to cart</button>
        <button type="button" class="button button--lg product-quick-view__shop-now" data-quick-view-bundle-buy-now>Shop now</button>
      </div>`;
    return bar;
  }

  _beforeProductRerender(event) {
    const scope = this._scopeFor(event.target);
    const state = this._captureState(scope);
    const handle = scope?.dataset.quickViewBundlePanel;
    requestAnimationFrame(() => {
      const nextScope = handle ? this.querySelector(`[data-quick-view-bundle-panel="${CSS.escape(handle)}"]`) : this;
      this._initializeContent(state, nextScope);
      this._updateBundleSummary();
    });
  }

  _captureState(scope = this) {
    const main = scope.querySelector('.product-quick-view__main');
    const info = scope.querySelector('.product-quick-view__info');
    const form = scope.querySelector('form[id^="product-quick-view-form-"]');
    return {
      mainScrollTop: main?.scrollTop || 0,
      infoScrollTop: info?.scrollTop || 0,
      quantity: form?.elements.namedItem('quantity')?.value || 1,
      variantsExpanded: scope.querySelector('[data-quick-view-variants]')?.classList.contains('is-expanded') || false,
      featuresExpanded: scope.querySelector('[data-quick-view-features]')?.classList.contains('is-expanded') || false
    };
  }

  _initializeVisibleContent(state = {}) {
    if (this.classList.contains('product-quick-view--bundle')) {
      this.querySelectorAll('[data-quick-view-bundle-panel]').forEach((panel) => this._initializeContent(panel.hidden ? {} : state, panel));
      this._applyBundleOperatorConfig();
      this._updateBundleSummary();
      this._syncBundleFeatureDock();
    } else {
      this._initializeContent(state, this);
    }
  }

  _initializeContent(state = {}, scope = this) {
    if (!scope) return;
    this._applyOperatorConfig(scope);
    const variants = scope.querySelector('[data-quick-view-variants]');
    const variantsToggle = scope.querySelector('[data-quick-view-toggle="variants"]');
    variants?.classList.toggle('is-expanded', Boolean(state.variantsExpanded));
    this._syncToggle(variantsToggle, Boolean(state.variantsExpanded));
    const features = scope.querySelector('[data-quick-view-features]');
    const featuresToggle = scope.querySelector('[data-quick-view-toggle="features"]');
    if (features && featuresToggle) {
      features.classList.remove('is-expanded');
      featuresToggle.hidden = false;
      const expanded = Boolean(state.featuresExpanded);
      features.classList.toggle('is-expanded', expanded);
      this._syncToggle(featuresToggle, expanded);
    }
    this._configureExternalVideo(scope);
    const main = scope.querySelector('.product-quick-view__main');
    const info = scope.querySelector('.product-quick-view__info');
    const form = scope.querySelector('form[id^="product-quick-view-form-"]');
    if (main) main.scrollTop = Number(state.mainScrollTop) || 0;
    if (info) info.scrollTop = features?.classList.contains('is-expanded') ? Number(state.infoScrollTop) || 0 : 0;
    if (form?.elements.namedItem('quantity')) form.elements.namedItem('quantity').value = state.quantity || 1;
    this._updateTotalPrice(scope);
    const carousel = scope.querySelector('media-carousel');
    if (carousel) this._syncMediaState(carousel.selectedIndex, scope);
  }

  _operatorConfig() {
    return document.querySelector('[data-product-quick-view-config]');
  }

  _applyOperatorConfig(scope = this) {
    const config = this._operatorConfig();
    if (!config) return;
    const setText = (selector, value) => {
      if (value) scope.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
    };
    const setIcon = (selector, url) => {
      if (!url) return;
      scope.querySelectorAll(selector).forEach((container) => {
        const image = document.createElement('img');
        Object.assign(image, { src: url, alt: '', width: 16, height: 16 });
        container.replaceChildren(image);
      });
    };
    setText('[data-quick-view-overview-label]', config.dataset.overviewLabel);
    setIcon('[data-quick-view-overview-icon]', config.dataset.overviewIcon);
    setText('[data-quick-view-video-label]', config.dataset.videoLabel);
    setIcon('[data-quick-view-video-icon]', config.dataset.videoIcon);
    setText('[data-quick-view-warranty-label]', config.dataset.warrantyLabel);
    setIcon('[data-quick-view-warranty-icon]', config.dataset.warrantyIcon);
    setText('[data-quick-view-money-back-label]', config.dataset.moneyBackLabel);
    setIcon('[data-quick-view-money-back-icon]', config.dataset.moneyBackIcon);
    setText('[data-quick-view-features-label]', config.dataset.featuresLabel);
    const featuresToggle = scope.querySelector('[data-quick-view-toggle="features"]');
    if (featuresToggle) {
      featuresToggle.dataset.moreLabel = config.dataset.showMoreLabel || featuresToggle.dataset.moreLabel;
      featuresToggle.dataset.lessLabel = config.dataset.showLessLabel || featuresToggle.dataset.lessLabel;
      this._syncToggle(featuresToggle, featuresToggle.getAttribute('aria-expanded') === 'true');
    }
    const addButton = scope.querySelector('.product-quick-view__actions form .button:not([disabled])');
    if (addButton && config.dataset.addToCartLabel) addButton.textContent = config.dataset.addToCartLabel;
    setText('[data-quick-view-shop-now-label]', config.dataset.shopNowLabel);
  }

  _applyBundleOperatorConfig() {
    const config = this._operatorConfig();
    if (!config) return;
    const setText = (selector, value) => {
      const element = this.querySelector(selector);
      if (element && value) element.textContent = value;
    };
    const totalLabel = config.dataset.bundleSubtotalLabel === 'Subtotal' ? 'Total' : config.dataset.bundleSubtotalLabel;
    setText('[data-quick-view-bundle-subtotal-label]', totalLabel || 'Total');
    setText('[data-quick-view-bundle-add]', config.dataset.bundleAddLabel);
    setText('[data-quick-view-bundle-buy-now]', config.dataset.shopNowLabel);
    const dockToggle = this.querySelector('[data-quick-view-bundle-feature-toggle]');
    if (dockToggle) {
      dockToggle.dataset.moreLabel = config.dataset.showMoreLabel || dockToggle.dataset.moreLabel;
      dockToggle.dataset.lessLabel = config.dataset.showLessLabel || dockToggle.dataset.lessLabel;
    }
  }

  _onQuickViewClick(event) {
    const tab = event.target.closest('[data-quick-view-bundle-tab]');
    if (tab) return this._selectBundleTab(tab.dataset.quickViewBundleTab, true);
    const bundleQuantityButton = event.target.closest('[data-quick-view-bundle-quantity-change]');
    if (bundleQuantityButton) {
      const input = this.querySelector('[data-quick-view-bundle-quantity]');
      const nextQuantity = Math.max(1, (Number(input?.value) || 1) + Number(bundleQuantityButton.dataset.quickViewBundleQuantityChange));
      if (input) input.value = String(nextQuantity);
      this._updateBundleSummary();
      return;
    }
    const bundleAdd = event.target.closest('[data-quick-view-bundle-add]');
    const bundleBuyNow = event.target.closest('[data-quick-view-bundle-buy-now]');
    if (bundleAdd || bundleBuyNow) return this._addBundle({ checkout: Boolean(bundleBuyNow) });
    const bundleFeatureToggle = event.target.closest('[data-quick-view-bundle-feature-toggle]');
    if (bundleFeatureToggle) {
      const scope = this._activeScope();
      const features = scope.querySelector('[data-quick-view-features]');
      const expanded = !features?.classList.contains('is-expanded');
      features?.classList.toggle('is-expanded', expanded);
      const originalToggle = scope.querySelector('[data-quick-view-toggle="features"]');
      if (originalToggle) this._syncToggle(originalToggle, expanded);
      this._syncBundleFeatureDock();
      return;
    }

    const scope = this._scopeFor(event.target);
    if (event.target.closest('.product-quick-view__quantity .quantity-selector__button')) {
      requestAnimationFrame(() => {
        this._updateTotalPrice(scope);
        this._updateBundleSummary();
      });
      return;
    }
    const externalVideoThumbnail = event.target.closest('[data-quick-view-video-index]');
    if (externalVideoThumbnail) {
      return this._showExternalVideo(scope.querySelector('[data-quick-view-external-video-tab]'), Number(externalVideoThumbnail.dataset.quickViewVideoIndex), scope);
    }
    const externalVideoButton = event.target.closest('[data-quick-view-external-video-tab]');
    if (externalVideoButton) return this._showExternalVideo(externalVideoButton, 0, scope);
    const mediaButton = event.target.closest('[data-quick-view-media-index]');
    if (mediaButton) {
      this._hideExternalVideo(scope);
      const mediaIndex = Number(mediaButton.dataset.quickViewMediaIndex);
      scope.querySelector('media-carousel')?.select(mediaIndex);
      this._syncMediaState(mediaIndex, scope);
      return;
    }
    const toggle = event.target.closest('[data-quick-view-toggle]');
    if (toggle) {
      const target = toggle.dataset.quickViewToggle === 'variants'
        ? scope.querySelector('[data-quick-view-variants]') : scope.querySelector('[data-quick-view-features]');
      const expanded = !target?.classList.contains('is-expanded');
      target?.classList.toggle('is-expanded', expanded);
      this._syncToggle(toggle, expanded);
      if (toggle.dataset.quickViewToggle === 'features') this._syncBundleFeatureDock();
      if (toggle.dataset.quickViewToggle === 'features' && !expanded) {
        const info = scope.querySelector('.product-quick-view__info');
        if (info) info.scrollTop = 0;
      }
      return;
    }
    const buyNowButton = event.target.closest('[data-quick-view-buy-now]');
    if (!buyNowButton || buyNowButton.disabled) return;
    const form = document.getElementById(buyNowButton.dataset.form);
    const variantId = form?.elements.namedItem('id')?.value;
    const quantity = Math.max(1, Number(form?.elements.namedItem('quantity')?.value) || 1);
    if (variantId) window.location.href = `${window.Shopify.routes.root}cart/${variantId}:${quantity}`;
  }

  _selectBundleTab(handle, focus = false) {
    this.querySelectorAll('[data-quick-view-bundle-tab]').forEach((tab) => {
      const selected = tab.dataset.quickViewBundleTab === handle;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
    this.querySelectorAll('[data-quick-view-bundle-panel]').forEach((panel) => {
      const selected = panel.dataset.quickViewBundlePanel === handle;
      panel.hidden = !selected;
      if (!selected) {
        this._hideExternalVideo(panel);
        panel.querySelectorAll('video-media, model-media').forEach((media) => media.pause());
      }
    });
    this._syncBundleFeatureDock();
    this._playSelectedMedia(this._activeScope());
  }

  _syncBundleFeatureDock() {
    const dock = this.querySelector('[data-quick-view-bundle-feature-dock]');
    const dockToggle = dock?.querySelector('[data-quick-view-bundle-feature-toggle]');
    const scope = this._activeScope();
    const features = scope?.querySelector('[data-quick-view-features]');
    const originalToggle = scope?.querySelector('[data-quick-view-toggle="features"]');
    if (!dock || !dockToggle) return;
    const expanded = Boolean(features?.classList.contains('is-expanded'));
    dock.hidden = false;
    dockToggle.hidden = !features || !originalToggle || expanded;
    this._syncToggle(dockToggle, expanded);
  }

  _onQuickViewKeydown(event) {
    const tab = event.target.closest('[data-quick-view-bundle-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = Array.from(this.querySelectorAll('[data-quick-view-bundle-tab]'));
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    event.preventDefault();
    this._selectBundleTab(tabs[nextIndex].dataset.quickViewBundleTab, true);
  }

  _onCarouselChanged(event) {
    if (event.target.matches('media-carousel')) this._syncMediaState(event.detail.index, this._scopeFor(event.target));
  }

  _syncMediaState(index, scope = this) {
    const current = scope.querySelector('[data-quick-view-current-media]');
    if (current) current.textContent = String(index + 1);
    scope.querySelectorAll('[data-quick-view-media-index]').forEach((button) => {
      button.setAttribute('aria-pressed', Number(button.dataset.quickViewMediaIndex) === index ? 'true' : 'false');
    });
  }

  _onQuickViewInput(event) {
    if (event.target.matches('[data-quick-view-bundle-quantity]')) {
      event.target.value = String(Math.max(1, Number(event.target.value) || 1));
      this._updateBundleSummary();
      return;
    }
    if (!event.target.matches('.product-quick-view__quantity input[name="quantity"]')) return;
    const scope = this._scopeFor(event.target);
    this._updateTotalPrice(scope);
    this._updateBundleSummary();
  }

  _updateTotalPrice(scope = this) {
    const priceContainer = scope.querySelector('[data-quick-view-unit-price]');
    const quantityInput = scope.querySelector('.product-quick-view__quantity input[name="quantity"]');
    if (!priceContainer || !quantityInput) return;
    const quantity = Math.max(1, Number(quantityInput.value) || 1);
    const unitPrice = Number(priceContainer.dataset.quickViewUnitPrice) || 0;
    const unitComparePrice = Number(priceContainer.dataset.quickViewUnitComparePrice) || 0;
    this._replacePriceText(priceContainer.querySelector('sale-price'), this._formatMoney(unitPrice * quantity));
    if (unitComparePrice > unitPrice) this._replacePriceText(priceContainer.querySelector('compare-at-price'), this._formatMoney(unitComparePrice * quantity));
  }

  _updateBundleSummary() {
    if (!this.classList.contains('product-quick-view--bundle')) return;
    const quantity = Math.max(1, Number(this.querySelector('[data-quick-view-bundle-quantity]')?.value) || 1);
    const unitPrice = Number(this._bundleParent?.price) || 0;
    const unitComparePrice = Number(this._bundleParent?.compareAtPrice) || 0;
    const total = unitPrice * quantity;
    const compareTotal = unitComparePrice * quantity;

    this.querySelector('[data-quick-view-bundle-total]').textContent = this._formatMoney(total);
    const compare = this.querySelector('[data-quick-view-bundle-compare]');
    compare.hidden = compareTotal <= total;
    compare.textContent = compareTotal > total ? this._formatMoney(compareTotal) : '';
    this.querySelectorAll('[data-quick-view-bundle-tab]').forEach((tab) => { tab.textContent = tab.dataset.quickViewBundleName; });
    this.querySelectorAll('[data-quick-view-bundle-add], [data-quick-view-bundle-buy-now]').forEach((button) => { button.disabled = !this._bundleParent?.available; });
  }

  _selectedVariant(scope) {
    try {
      return JSON.parse(scope.querySelector('variant-picker script[data-variant]')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  async _addBundle({ checkout = false } = {}) {
    const buttons = Array.from(this.querySelectorAll('[data-quick-view-bundle-add], [data-quick-view-bundle-buy-now]'));
    const errorElement = this.querySelector('[data-quick-view-bundle-error]');
    buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    if (errorElement) errorElement.hidden = true;
    try {
      const id = Number(this._bundleParent?.variantId);
      const quantity = Math.max(1, Number(this.querySelector('[data-quick-view-bundle-quantity]')?.value) || 1);
      if (!id || !this._bundleParent?.available) throw new Error('This bundle is unavailable.');
      const items = [{ id, quantity }];
      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname })
      });
      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || 'Unable to add this bundle to cart.');
      if (checkout) return window.location.href = `${window.Shopify.routes.root}checkout`;
      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        return window.location.href = `${window.Shopify.routes.root}cart`;
      }
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = responseJson.sections;
      await this.hide();
      document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: responseJson.items || [], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (error) {
      if (errorElement) { errorElement.textContent = error.message; errorElement.hidden = false; }
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
      this._updateBundleSummary();
    }
  }

  _replacePriceText(element, value) {
    if (!element) return;
    Array.from(element.childNodes).forEach((node) => { if (node.nodeType === Node.TEXT_NODE) node.remove(); });
    element.append(document.createTextNode(value));
  }

  _formatMoney(cents) {
    return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      currencyDisplay: window.themeVariables?.settings?.currencyCodeEnabled ? 'code' : 'narrowSymbol'
    }).format(cents / 100);
  }

  _scopeFor(element) { return element?.closest?.('[data-quick-view-bundle-panel]') || this; }
  _activeScope() { return this.querySelector('[data-quick-view-bundle-panel]:not([hidden])') || this; }
  _externalVideosFor(scope) {
    const handle = scope?.dataset?.quickViewBundlePanel;
    return handle ? this._externalVideosByHandle.get(handle) || [] : this._externalVideos || [];
  }

  _configureExternalVideo(scope = this) {
    const button = scope.querySelector('[data-quick-view-external-video-tab]');
    const thumbnails = scope.querySelector('[data-quick-view-video-thumbnails]');
    if (!button || !thumbnails) return;
    const videos = this._externalVideosFor(scope);
    const nativeVideoButton = scope.querySelector('[data-quick-view-native-video-tab]');
    button.hidden = videos.length === 0;
    if (nativeVideoButton) nativeVideoButton.hidden = videos.length > 0;
    thumbnails.replaceChildren();
    videos.forEach((video, index) => {
      const thumbnailButton = document.createElement('button');
      thumbnailButton.type = 'button';
      thumbnailButton.className = 'product-quick-view__thumbnail';
      thumbnailButton.dataset.quickViewVideoIndex = String(index);
      thumbnailButton.setAttribute('aria-label', `${video.title} ${index + 1}`);
      thumbnailButton.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      if (video.thumbnail) {
        const image = document.createElement('img');
        Object.assign(image, { src: video.thumbnail, alt: '', loading: 'lazy' });
        thumbnailButton.append(image);
      }
      thumbnails.append(thumbnailButton);
    });
  }

  _showExternalVideo(button, index = 0, scope = this) {
    const player = scope.querySelector('[data-quick-view-external-video-player]');
    const mediaList = scope.querySelector('.product-quick-view__media-list');
    const video = this._externalVideosFor(scope)[index];
    if (!button || !player || !mediaList || !video) return;
    scope.querySelectorAll('.product-quick-view__media-tabs button').forEach((tab) => tab.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');
    scope.querySelectorAll('video-media, model-media').forEach((media) => media.pause());
    mediaList.hidden = true;
    scope.querySelectorAll('.product-quick-view__nav').forEach((nav) => nav.hidden = true);
    player.hidden = false;
    scope.querySelector('.product-quick-view__thumbnails:not(.product-quick-view__video-thumbnails)')?.setAttribute('hidden', '');
    const videoThumbnails = scope.querySelector('[data-quick-view-video-thumbnails]');
    if (videoThumbnails) videoThumbnails.hidden = false;
    videoThumbnails?.querySelectorAll('[data-quick-view-video-index]').forEach((thumbnail) => {
      thumbnail.setAttribute('aria-current', Number(thumbnail.dataset.quickViewVideoIndex) === index ? 'true' : 'false');
    });
    player.replaceChildren();
    const youtubeId = this._getYoutubeId(video.url);
    if (youtubeId) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&playsinline=1&enablejsapi=1&rel=0`;
      iframe.allow = 'autoplay; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.title = video.title;
      player.append(iframe);
    } else {
      const videoElement = document.createElement('video');
      Object.assign(videoElement, { src: video.url, controls: true, autoplay: true, muted: true, playsInline: true });
      player.append(videoElement);
      videoElement.play().catch(() => {});
    }
  }

  _hideExternalVideo(scope = this) {
    const player = scope.querySelector('[data-quick-view-external-video-player]');
    const mediaList = scope.querySelector('.product-quick-view__media-list');
    if (!player || !mediaList || player.hidden) return;
    player.hidden = true;
    player.replaceChildren();
    mediaList.hidden = false;
    scope.querySelectorAll('.product-quick-view__nav').forEach((nav) => nav.hidden = false);
    scope.querySelector('.product-quick-view__thumbnails:not(.product-quick-view__video-thumbnails)')?.removeAttribute('hidden');
    const thumbnails = scope.querySelector('[data-quick-view-video-thumbnails]');
    if (thumbnails) thumbnails.hidden = true;
    scope.querySelector('[data-quick-view-external-video-tab]')?.setAttribute('aria-pressed', 'false');
  }

  _getYoutubeId(url) {
    return url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&"'>]+)/)?.[1] || null;
  }

  _syncToggle(button, expanded) {
    if (!button) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
  }

  _playSelectedMedia(scope = this) {
    const media = scope?.querySelector('media-carousel[autoplay]')?.selectedSlide;
    if (['video', 'external_video'].includes(media?.dataset.mediaType)) media.firstElementChild.play();
  }
}

if (!window.customElements.get('product-quick-view')) window.customElements.define('product-quick-view', ProductQuickView);

if (window.Shopify?.designMode && !window.__campaignQuickViewEditorPreviewBound) {
  window.__campaignQuickViewEditorPreviewBound = true;
  document.addEventListener('shopify:section:select', async (event) => {
    const config = event.target.querySelector('[data-product-quick-view-config]');
    if (!config) return;
    await window.customElements.whenDefined('product-quick-view');
    (document.getElementById(config.dataset.previewTarget) || document.querySelector('product-quick-view[handle]'))?.show();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const config = event.target.querySelector('[data-product-quick-view-config]');
    if (config) document.getElementById(config.dataset.previewTarget)?.hide();
  });
}
